

/**
 * TimeLeft component - Displays countdown timer for OTP expiration
 * Handles timer countdown logic and shows expiration popup when time runs out
 */
import { useTranslations } from "next-intl";
import { ExpiredOtpPopup } from "./PuExpiredOtp";
import { useEffect, useState } from "react";
import { formatSecondsToMinutes } from "@/lib/utils/parser";

// Props type definition for the component
type TimeLeftProps = {
  timeLeft: number;
};

/**
 * Displays a countdown timer showing remaining time for the OTP
 * When timer expires, displays an expiration popup
 * @param timeLeft - Initial time left in seconds
 */
export const TimeLeft: React.FC<TimeLeftProps> = ({ timeLeft }) => {
  // Get translations for the component
  const t = useTranslations("changePassword");
  // State to track remaining time
  const [timeLeftState, setTimeLeftState] = useState(timeLeft);
  // State to control popup visibility
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  // OTP expiration logic
  useEffect(() => {
    // Only set up the timer once when the component mounts
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeftState((prevTime) => {
          // Use the functional update form to get the latest state
          if (prevTime <= 1) {
            clearInterval(timer);
            setIsPopupVisible(true);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000); // Update every second

      // Clean up interval on component unmount
      return () => clearInterval(timer);
    } else {
      // If already expired, show popup immediately
      setIsPopupVisible(true);
    }
  }, []); // Empty dependency array means this runs once on mount

  return (
    <>
      <p className="body-1-regular text-left w-full">
        {t("changePasswordDescription1")}
        <span className={`body-1-bold`}>
          {" "}
          {isPopupVisible ? "00:00" : formatSecondsToMinutes(timeLeftState)}
        </span>
        {t("changePasswordDescription2")}
      </p>
      {/* Show expiration popup when time runs out */}
      {isPopupVisible && <ExpiredOtpPopup />}
    </>
  );
};
